// src/routes/MediaSelection/MediaSelectionForm.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { designTokens } from '../../theme/designTokens';
import { bg, text as textColors, border, shadow, overlay, statusBg, adaptiveColor } from '../../utils/darkModeAdapter';
import { PREFECTURES, parseLocation, buildLocation } from '../../constants/prefectures';
import { saveCommonFields, loadCommonFields } from '../../utils/commonProfileStore';

// スタイルされたコンポーネント
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 12,
  backgroundColor: bg(theme).card,
  boxShadow: designTokens.shadows.sm,
  border: `1px solid ${border(theme).light}`,
  maxWidth: 720,
  margin: '0 auto',
  position: 'relative',
  overflow: 'hidden',
  transition: designTokens.transitions.normal,
  '&:hover': {
    boxShadow: designTokens.shadows.md,
  },
}));

const FormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  fontWeight: 700,
  color: theme.palette.text.primary,
  fontSize: '1.5rem',
  letterSpacing: '-0.02em',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    transition: 'all 0.2s ease',
    backgroundColor: bg(theme).card,
    '&:hover': {
      boxShadow: designTokens.shadows.sm,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 2px ${designTokens.colors.primary}22`,
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    color: theme.palette.text.secondary,
    '&.Mui-focused': {
      color: designTokens.colors.primary,
    }
  },
  '& .MuiFormHelperText-root': {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  }
}));

const BudgetChip = styled(Chip)(({ theme }) => ({
  backgroundColor: statusBg(theme).info,
  color: theme.palette.primary.main,
  fontWeight: 'bold',
  fontSize: '0.9rem',
  height: '28px',
  '& .MuiChip-label': {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  }
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  minWidth: 140,
  padding: theme.spacing(1.5, 3),
  borderRadius: 8,
  fontSize: '1rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
  backgroundColor: designTokens.colors.primary,
  boxShadow: designTokens.shadows.sm,
  transition: 'all 0.2s ease',
  textTransform: 'none',
  position: 'relative',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: designTokens.shadows.md,
  },
  '&:active': {
    boxShadow: designTokens.shadows.sm,
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
    boxShadow: 'none',
  },
  // MUIのリップルエフェクトを強化
  '& .MuiTouchRipple-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  }
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: 8,
  backgroundColor: bg(theme).subtle,
  boxShadow: 'none',
  border: `1px solid ${border(theme).light}`,
  '&::before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    borderRadius: 8,
    '&:hover': {
      backgroundColor: bg(theme).tinted,
    }
  }
}));

/**
 * 必須項目:
 *   - 職種 (position)
 *   - 希望採用人数 (targetHires)
 *   - ターゲット人材層 (targetCandidateGroup)
 *   - 媒体広告費 (advertisingBudget)
 *   - 勤務地 (location)
 *   - 採用完了希望日 (desiredCompletionDate)
 *
 * 任意入力項目:
 *   - 採用予算の上限 (budgetUpperLimit)
 *   - 希望する媒体 (preferredMedia)
 *   - 広告掲載期間 (adDuration)
 *   - 過去に使用した媒体／運用実績 (previousMediaUsage)
 */

export default function MediaSelectionForm({ defaultValues, onSubmit, onBack }) {
  const theme = useTheme();

  // 人材層の選択肢
  const candidateOptions = [
    { value: '新卒', label: '新卒' },
    { value: '第二新卒', label: '第二新卒' },
    { value: 'ミドル層', label: 'ミドル層（3-10年経験）' },
    { value: 'シニア層', label: 'シニア層（10年以上経験）' },
    { value: '管理職候補', label: '管理職候補' },
    { value: 'エグゼクティブ', label: 'エグゼクティブ' },
    { value: 'パート・アルバイト', label: 'パート・アルバイト' },
    { value: 'インターン', label: 'インターン' },
  ];

  // ---------------------------
  // (1) ステート初期化
  // ---------------------------
  const saved = loadCommonFields();
  const initialFormData = {
    position: '',
    targetHires: '',
    advertisingBudget: '',
    location: '',
    desiredCompletionDate: '',
    budgetUpperLimit: '',
    targetCandidateGroup: '',
    preferredMedia: '',
    adDuration: '',
    previousMediaUsage: '',
    ...defaultValues,  // 親から渡ってきた初期値をマージ
  };
  // 親から値が渡されていない場合のみ、共通プロフィールで補完
  if (!initialFormData.position) initialFormData.position = saved.position || '';
  if (!initialFormData.location) initialFormData.location = saved.location || '';

  const [formData, setFormData] = useState(initialFormData);
  const [optionalOpen, setOptionalOpen] = useState(false);

  // ---------------------------
  // (2) 必須項目が未入力なら赤背景にする
  // ---------------------------
  const requiredFieldStyle = (val) => {
    const strVal = typeof val === 'string' ? val.trim() : '';
    return {
      backgroundColor: strVal === '' ? adaptiveColor(theme, 'rgba(255,248,248,0.8)', 'rgba(60,30,30,0.8)') : adaptiveColor(theme, 'rgba(255,255,255,0.9)', 'rgba(30,30,30,0.9)'),
      borderRadius: '12px',
    };
  };

  // ---------------------------
  // (3) 入力ハンドラ (数字のみに制限するフィールドあり)
  // ---------------------------
  const handleChange = (e) => {
    let { name, value } = e.target;
    if (['targetHires', 'advertisingBudget', 'budgetUpperLimit'].includes(name)) {
      // 数字以外を除去
      value = value.replace(/\D/g, '');
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ---------------------------
  // (4) 送信可否チェック
  // ---------------------------
  const canSubmit = () => {
    const isEmpty = (v) => !v || !v.trim();
    // 必須6項目が埋まっているか
    if (
      isEmpty(formData.position) ||
      isEmpty(formData.targetHires) ||
      isEmpty(formData.targetCandidateGroup) ||
      isEmpty(formData.advertisingBudget) ||
      isEmpty(formData.location) ||
      isEmpty(formData.desiredCompletionDate)
    ) {
      return false;
    }
    return true;
  };

  // ---------------------------
  // (5) フォーム送信 (event)
  // ---------------------------
  const handleLocalSubmit = (event) => {
    event.preventDefault(); // ← リロード抑止

    // 必須項目チェック
    if (!canSubmit()) {
      alert("必須項目（職種／希望人数／ターゲット人材層／媒体広告費／勤務地／採用完了希望日）が未入力です。");
      return;
    }

    // 共通プロフィールに保存
    saveCommonFields({ position: formData.position, location: formData.location });

    // 親コンポーネントに formData を渡す
    onSubmit(formData);
  };

  // 金額の表示形式を変換（例：500000 → 50万円）
  const formatBudget = (value) => {
    if (!value) return '';
    const num = parseInt(value);
    if (isNaN(num) || num === 0) return '';
    if (num < 10000) return `${num.toLocaleString()}円`;
    return `${Math.floor(num / 10000)}万円`;
  };

  // ---------------------------
  // (6) レンダリング
  // ---------------------------
  return (
      <StyledPaper
        component={motion.div}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <Box sx={{ position: 'relative', mb: 2 }}>
          {onBack && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              size="small"
              sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', color: 'text.secondary', minWidth: 'auto', whiteSpace: 'nowrap' }}
            >
              戻る
            </Button>
          )}
          <FormTitle variant="h6" sx={{ mb: 0, textAlign: 'center' }}>
            媒体選定フォーム
          </FormTitle>
        </Box>

      {/* ここで onSubmit に handleLocalSubmit を紐づける */}
      <Box component="form" onSubmit={handleLocalSubmit} noValidate>
        {/* ------ 必須項目 ------ */}
        <StyledTextField
          label="職種 (必須)"
          name="position"
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            style: requiredFieldStyle(formData.position)
          }}
          value={formData.position}
          onChange={handleChange}
          placeholder="例：エンジニア、営業、マーケティング担当など"
          helperText="募集する職種名・ポジション名を具体的に入力してください。例：バックエンドエンジニア、人事担当、経理アシスタントなど"
        />

        <StyledTextField
          label="希望採用人数 (必須)"
          name="targetHires"
          type="text"
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            style: requiredFieldStyle(formData.targetHires)
          }}
          value={formData.targetHires}
          onChange={handleChange}
          placeholder="例：3名、、5名など"
          helperText="今回の採用で確保したい人数を半角数字で入力してください。例：3名採用したい場合は「3」と入力"
        />

        <StyledTextField
          select
          label="ターゲット人材層 (必須)"
          name="targetCandidateGroup"
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            style: requiredFieldStyle(formData.targetCandidateGroup)
          }}
          value={formData.targetCandidateGroup}
          onChange={handleChange}
          helperText="採用ターゲットとなる人材層を選択してください"
        >
          {candidateOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </StyledTextField>

        <Box sx={{ mb: 2 }}>
          <StyledTextField
            label="媒体広告費（円）(必須)"
            name="advertisingBudget"
            type="text"
            fullWidth
            InputProps={{
              style: requiredFieldStyle(formData.advertisingBudget),
              endAdornment: formData.advertisingBudget && (
                <BudgetChip
                  label={formatBudget(formData.advertisingBudget)}
                  size="small"
                />
              )
            }}
            value={formData.advertisingBudget}
            onChange={handleChange}
            helperText="求人媒体への広告掲載にかける予算を円単位で入力してください。例：100万円の場合「1000000」"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl
            variant="outlined"
            sx={{ minWidth: 160 }}
          >
            <InputLabel>都道府県 *</InputLabel>
            <Select
              label="都道府県 *"
              value={parseLocation(formData.location).prefecture}
              onChange={(e) => {
                const { detail } = parseLocation(formData.location);
                const newLocation = buildLocation(e.target.value, detail);
                handleChange({ target: { name: 'location', value: newLocation } });
              }}
              sx={{
                borderRadius: 2,
                ...requiredFieldStyle(parseLocation(formData.location).prefecture)
              }}
            >
              <MenuItem value="">選択してください</MenuItem>
              {PREFECTURES.map((pref) => (
                <MenuItem key={pref} value={pref}>{pref}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <StyledTextField
            label="補足（市区町村など）"
            fullWidth
            value={parseLocation(formData.location).detail}
            onChange={(e) => {
              const { prefecture } = parseLocation(formData.location);
              const newLocation = buildLocation(prefecture, e.target.value);
              handleChange({ target: { name: 'location', value: newLocation } });
            }}
            placeholder="例: 渋谷区、リモート可など"
            helperText="市区町村、リモート可否など"
          />
        </Box>

        <StyledTextField
          label="採用完了希望日 (必須)"
          name="desiredCompletionDate"
          type="date"
          fullWidth
          sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            style: requiredFieldStyle(formData.desiredCompletionDate)
          }}
          value={formData.desiredCompletionDate}
          onChange={handleChange}
          helperText="いつまでに採用を完了したいか、目標日を設定してください。例：4月入社に向けて3月末までに採用完了など"
        />

        {/* ------ 任意入力項目: Accordionで隠す ------ */}
        <StyledAccordion
          expanded={optionalOpen}
          onChange={() => setOptionalOpen(!optionalOpen)}
          sx={{ mt: 3 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>
              {optionalOpen ? '任意入力項目(クリックで閉じる)' : '任意入力項目(クリックで開く)'}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 2 }}>
              <StyledTextField
                label="採用予算の上限（円）"
                name="budgetUpperLimit"
                type="text"
                fullWidth
                InputProps={{
                  endAdornment: formData.budgetUpperLimit && (
                    <BudgetChip
                      label={formatBudget(formData.budgetUpperLimit)}
                      size="small"
                    />
                  )
                }}
                value={formData.budgetUpperLimit}
                onChange={handleChange}
                helperText="採用活動全体にかけられる最大予算を円単位で入力してください。広告費以外の費用も含む"
              />
            </Box>


            <StyledTextField
              label="希望する媒体（複数可）"
              name="preferredMedia"
              fullWidth
              sx={{ mb: 2 }}
              value={formData.preferredMedia}
              onChange={handleChange}
              placeholder="例：リクナビ、Green、Wantedlyなど"
              helperText="使いたい求人媒体があれば記載してください。複数ある場合はカンマ区切りで入力。例：リクナビNEXT、マイナビ、doda、Green、Wantedlyなど"
            />

            <StyledTextField
              label="広告掲載期間"
              name="adDuration"
              fullWidth
              sx={{ mb: 2 }}
              value={formData.adDuration}
              onChange={handleChange}
              placeholder="例：2ヶ月、、3ヶ月など"
              helperText="求人広告を掲載する期間を記載してください。例：1ヶ月、、3ヶ月、半年、採用決定までなど"
            />

            <StyledTextField
              label="過去に使用した媒体や運用実績"
              name="previousMediaUsage"
              fullWidth
              multiline
              minRows={2}
              sx={{ mb: 2 }}
              value={formData.previousMediaUsage}
              onChange={handleChange}
              placeholder="例：リクナビNEXTで3名採用、Greenで2名応募など"
              helperText="これまでに使用した求人媒体での実績や効果を記載してください。例：リクナビで年間5名採用、Wantedlyからの応募は少なかった、ハローワークは効果的だった、人材紹介会社A社から3名採用実績ありなど"
            />
          </AccordionDetails>
        </StyledAccordion>

        {/* ------ 送信ボタン ------ */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <SubmitButton 
            type="submit" 
            variant="contained" 
            disabled={!canSubmit()}
            component={motion.button}
            whileHover={{ scale: !canSubmit() ? 1 : 1.02 }}
            whileTap={{ scale: !canSubmit() ? 1 : 0.98 }}
          >
            送信
          </SubmitButton>
        </Box>
      </Box>
    </StyledPaper>
  );
}